/** The portal's one modal, for the two decisions that cannot be taken back.
 *
 * Revoking a token stops an installation uploading the moment it is pressed,
 * and deleting an account revokes every token at once. Neither has an undo,
 * so neither happens on a single click of a button that sits beside ordinary
 * ones: the consequence is written out first, in the same words the spec
 * uses, and the confirming button is the one that carries the colour.
 *
 * Everything else here is the accessibility a dialog owes its reader and
 * which a `<div>` with a shadow does not provide by itself — the focus moves
 * in on open and back to whatever opened it on close, Escape cancels, Tab
 * stays inside, and the surrounding page is marked `aria-hidden` by being
 * covered rather than by being described as still available. The busy state
 * disables both buttons rather than only the one pressed, because a cancel
 * half way through a delete is a request the server has already begun.
 */

import { useCallback, useEffect, useId, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { ErrorNote } from '@/components/ui';

export interface ConfirmDialogProps {
  /** The question, as a short sentence with no full stop. */
  title: ReactNode;
  /** What will happen. Written out, not summarised. */
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` for anything that destroys; `primary` for the rest. */
  tone?: 'danger' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
  /** True while the request is in flight: both buttons stop answering. */
  busy?: boolean;
  /** Whatever the confirmation threw, shown inside the dialog so the reader
   *  is not returned to the page with no explanation. */
  error?: unknown;
}

/** Everything inside the dialog that a Tab can reach. */
function focusable(root: HTMLElement): HTMLElement[] {
  const found = root.querySelectorAll<HTMLElement>(
    'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  return Array.from(found);
}

export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'danger',
  onConfirm,
  onCancel,
  busy,
  error,
}: ConfirmDialogProps) {
  const titleId = useId();
  const bodyId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  // Held in a ref so the key handler below does not have to be rebuilt every
  // time the parent re-renders with a fresh closure.
  const dismiss = useRef(onCancel);
  dismiss.current = onCancel;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancel.current?.focus();
    return () => opener?.focus();
  }, []);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      dismiss.current();
      return;
    }
    if (event.key !== 'Tab' || !panel.current) return;

    const stops = focusable(panel.current);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  return (
    <div
      className="dialog-backdrop"
      onKeyDown={onKeyDown}
      // Mousedown rather than click: a drag that starts inside the dialog and
      // ends on the backdrop is a selection, not a dismissal.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className="dialog"
      >
        <h2 className="dialog-title" id={titleId}>
          {title}
        </h2>
        <div className="dialog-body" id={bodyId} style={{ textWrap: 'pretty' }}>
          {children}
        </div>
        {error ? <ErrorNote error={error} compact /> : null}
        <div className="dialog-actions">
          <button
            type="button"
            ref={cancel}
            className="btn btn-secondary"
            disabled={busy}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={tone === 'danger' ? 'btn btn-danger' : 'btn btn-primary'}
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
